/**
 * MovieDetail Component
 * Displays detailed information about a movie including cast and crew
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  addMovieToPlaylist,
  type DiscoveryMovie,
  type FriendWhoWatched,
  getFriendsWhoWatchedMovie,
  getMovie,
  getMovieCredits,
  getPlaylist,
  getPlaylists,
  getSimilarMovies,
  getWatchedMovies,
  getWatchProviders,
  markMovieWatched,
  type Movie,
  type MovieCredits,
  removeMovieFromPlaylist,
  unmarkMovieWatched,
  type WatchProviders,
} from '../api';
import AddToPlaylistModal from './AddToPlaylistModal';
import CreateSimilarPlaylistModal from './CreateSimilarPlaylistModal';
import RatingBadges from './RatingBadges';
import TrailerModal from './TrailerModal';

const getLanguageName = (code: string): string => {
  try {
    const displayNames = new Intl.DisplayNames(['en'], { type: 'language' });
    return displayNames.of(code) || code.toUpperCase();
  } catch {
    return code.toUpperCase();
  }
};

export default function MovieDetail() {
  const { tmdbId } = useParams<{ tmdbId: string }>();
  const [movie, setMovie] = useState<Movie | null>(null);
  const [credits, setCredits] = useState<MovieCredits | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isWatched, setIsWatched] = useState(false);
  const [togglingWatched, setTogglingWatched] = useState(false);
  const [friendsWhoWatched, setFriendsWhoWatched] = useState<FriendWhoWatched[]>([]);
  const [similarMovies, setSimilarMovies] = useState<DiscoveryMovie[]>([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const [showSimilarPlaylistModal, setShowSimilarPlaylistModal] = useState(false);
  const [showAddToPlaylistModal, setShowAddToPlaylistModal] = useState(false);
  const [showTrailerModal, setShowTrailerModal] = useState(false);
  const [wantToWatchPlaylistId, setWantToWatchPlaylistId] = useState<number | null>(null);
  const [isInWantToWatch, setIsInWantToWatch] = useState(false);
  const [togglingWantToWatch, setTogglingWantToWatch] = useState(false);
  const [watchProviders, setWatchProviders] = useState<WatchProviders | null>(null);

  const movieId = tmdbId ? parseInt(tmdbId, 10) : null;

  useEffect(() => {
    async function loadMovie() {
      if (!movieId) return;

      try {
        setLoading(true);
        const country = (() => {
          try {
            const locale = Intl.DateTimeFormat().resolvedOptions().locale;
            const region = new Intl.Locale(locale).region;
            return region ?? 'US';
          } catch {
            return 'US';
          }
        })();
        const [movieData, creditsData, watchedData, friendsData, playlistsData, providersData] =
          await Promise.all([
            getMovie(movieId),
            getMovieCredits(movieId),
            getWatchedMovies(),
            getFriendsWhoWatchedMovie(movieId),
            getPlaylists(),
            getWatchProviders(movieId, country).catch(() => null),
          ]);
        setMovie(movieData);
        setCredits(creditsData);
        setIsWatched(watchedData.includes(movieId));
        setFriendsWhoWatched(friendsData);
        setWatchProviders(providersData);

        // Find the Want to Watch playlist and check if movie is in it
        const wtwPlaylist = playlistsData.playlists.find((p) => p.type === 'want_to_watch');
        if (wtwPlaylist) {
          setWantToWatchPlaylistId(wtwPlaylist.id);
          // Check if this movie is in the Want to Watch playlist
          try {
            const wtwDetail = await getPlaylist(wtwPlaylist.id, { limit: 100 });
            setIsInWantToWatch(wtwDetail.movies.some((m) => m.tmdb_id === movieId));
          } catch {
            // Non-critical, ignore
          }
        }

        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load movie');
      } finally {
        setLoading(false);
      }
    }

    loadMovie();
  }, [movieId]);

  // Load similar movies separately (non-blocking)
  useEffect(() => {
    async function loadSimilarMovies() {
      if (!movieId) return;

      try {
        setLoadingSimilar(true);
        const data = await getSimilarMovies(movieId, { limit: 15, includeRecommendations: true });
        setSimilarMovies(data.results);
      } catch (err) {
        // Silently fail - similar movies are optional
        console.error('Failed to load similar movies:', err);
      } finally {
        setLoadingSimilar(false);
      }
    }

    loadSimilarMovies();
  }, [movieId]);

  const handleToggleWantToWatch = async () => {
    if (!movieId || !wantToWatchPlaylistId) return;

    setTogglingWantToWatch(true);
    const wasInWtw = isInWantToWatch;

    // Optimistic update
    setIsInWantToWatch(!wasInWtw);

    try {
      if (wasInWtw) {
        await removeMovieFromPlaylist(wantToWatchPlaylistId, movieId);
      } else {
        await addMovieToPlaylist(wantToWatchPlaylistId, movieId);
      }
    } catch (err) {
      // Revert on error
      setIsInWantToWatch(wasInWtw);
      setError(err instanceof Error ? err.message : 'Failed to update Want to Watch');
    } finally {
      setTogglingWantToWatch(false);
    }
  };

  const handleToggleWatched = async () => {
    if (!movieId) return;

    setTogglingWatched(true);
    const wasWatched = isWatched;

    // Optimistic update
    setIsWatched(!wasWatched);

    try {
      if (wasWatched) {
        await unmarkMovieWatched(movieId);
      } else {
        await markMovieWatched(movieId);
        // Backend auto-removes from Want to Watch when marking as watched
        setIsInWantToWatch(false);
      }
    } catch (err) {
      // Revert on error
      setIsWatched(wasWatched);
      setError(err instanceof Error ? err.message : 'Failed to update watched status');
    } finally {
      setTogglingWatched(false);
    }
  };

  if (loading) {
    return <div className="empty">Loading movie...</div>;
  }

  if (error || !movie) {
    return (
      <div className="card">
        <div className="empty">
          <p>{error || 'Movie not found'}</p>
          <Link to="/" className="link-button">
            Back to Playlists
          </Link>
        </div>
      </div>
    );
  }

  const formatRuntime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="movie-detail">
      <div className="movie-detail-header">
        <button onClick={() => window.history.back()} className="link-button">
          ← Back
        </button>
      </div>

      {/* Hero section with backdrop */}
      {movie.backdrop_url && (
        <div className="movie-backdrop">
          <img src={movie.backdrop_url} alt="" />
          <div className="movie-backdrop-overlay" />
        </div>
      )}

      <div className="card movie-detail-content">
        {/* Top section: poster + basic info side by side */}
        <div className="movie-detail-body">
          {/* Poster */}
          <div className="movie-detail-poster">
            {movie.poster_url ? (
              <img src={movie.poster_url} alt={movie.title} />
            ) : (
              <div className="no-image detail-poster-placeholder">No image</div>
            )}

            {/* Friends who watched */}
            {friendsWhoWatched.length > 0 && (
              <div className="friends-watched-section">
                <span className="friends-watched-label">Watched by</span>
                <div className="friends-watched-avatars">
                  {friendsWhoWatched.slice(0, 5).map((friend) => (
                    <div
                      key={friend.id}
                      className="friend-avatar-wrapper"
                      title={friend.username || friend.name || 'Friend'}
                    >
                      {friend.picture ? (
                        <img
                          src={friend.picture}
                          alt={friend.username || friend.name || ''}
                          className="friend-avatar"
                        />
                      ) : (
                        <div className="friend-avatar placeholder">
                          {(friend.username || friend.name || '?')[0].toUpperCase()}
                        </div>
                      )}
                    </div>
                  ))}
                  {friendsWhoWatched.length > 5 && (
                    <div className="friends-watched-more">+{friendsWhoWatched.length - 5}</div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Basic Info */}
          <div className="movie-detail-info">
            <h1>
              {movie.original_title && movie.original_title !== movie.title
                ? movie.original_title
                : movie.title}
            </h1>

            {movie.original_title && movie.original_title !== movie.title && (
              <p className="movie-original-title">{movie.title}</p>
            )}

            <div className="movie-detail-meta">
              {movie.year && <span>{movie.year}</span>}
              {movie.runtime && <span>{formatRuntime(movie.runtime)}</span>}
              {movie.original_language && (
                <span className="language-badge">{getLanguageName(movie.original_language)}</span>
              )}
            </div>

            {/* Genres */}
            {movie.genres && movie.genres.length > 0 && (
              <div className="movie-genres">
                {movie.genres.map((genre) => (
                  <span key={genre.id} className="genre-badge">
                    {genre.name}
                  </span>
                ))}
              </div>
            )}

            {/* Ratings - only shown on desktop, moved below on mobile */}
            <div className="movie-ratings movie-ratings-desktop">
              <RatingBadges
                imdbRating={movie.imdb_rating}
                imdbVotes={movie.imdb_votes}
                rottenTomatoesRating={movie.rotten_tomatoes_rating}
                metacriticRating={movie.metacritic_rating}
                showVotes={true}
              />
            </div>
          </div>
        </div>

        {/* Action buttons - full width below poster/info grid */}
        <div className="movie-detail-actions">
          <button
            className={`watched-button ${isWatched ? 'watched' : ''}`}
            onClick={handleToggleWatched}
            disabled={togglingWatched}
          >
            {togglingWatched ? (
              <span className="action-spinner" />
            ) : isWatched ? (
              <>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
                Watched
              </>
            ) : (
              <>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                Mark as Watched
              </>
            )}
          </button>

          {!isWatched && wantToWatchPlaylistId && (
            <button
              className={`want-to-watch-button ${isInWantToWatch ? 'active' : ''}`}
              onClick={handleToggleWantToWatch}
              disabled={togglingWantToWatch}
            >
              {togglingWantToWatch ? (
                <span className="action-spinner" />
              ) : (
                <svg
                  viewBox="0 0 24 24"
                  fill={isInWantToWatch ? 'currentColor' : 'none'}
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                </svg>
              )}
              {isInWantToWatch ? 'Want to Watch' : 'Want to Watch'}
            </button>
          )}

          <button className="add-to-playlist-button" onClick={() => setShowTrailerModal(true)}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
            Watch Trailer
          </button>

          <button
            className="add-to-playlist-button"
            onClick={() => setShowAddToPlaylistModal(true)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add to Playlist
          </button>
        </div>

        {/* Ratings - mobile only, full width */}
        <div className="movie-ratings movie-ratings-mobile">
          <RatingBadges
            imdbRating={movie.imdb_rating}
            imdbVotes={movie.imdb_votes}
            rottenTomatoesRating={movie.rotten_tomatoes_rating}
            metacriticRating={movie.metacritic_rating}
            showVotes={true}
          />
        </div>

        {/* Where to Watch */}
        {watchProviders?.link && (
          <a
            href={watchProviders.link}
            target="_blank"
            rel="noopener noreferrer"
            className="watch-providers-link"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="7" width="20" height="15" rx="2" ry="2" />
              <polyline points="17 2 12 7 7 2" />
            </svg>
            Where to Watch
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="external-icon"
            >
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
          </a>
        )}

        {/* Overview - full width */}
        {movie.overview && (
          <div className="movie-overview-section">
            <h3>Overview</h3>
            <p>{movie.overview}</p>
          </div>
        )}

        {/* Similar Movies Section */}
        {(similarMovies.length > 0 || loadingSimilar) && (
          <div className="similar-movies-section">
            <div className="similar-movies-header">
              <h3>Similar Movies</h3>
            </div>

            {loadingSimilar ? (
              <div className="similar-movies-loading">
                <div className="spinner" />
                <span>Loading similar movies...</span>
              </div>
            ) : (
              <>
                <div className="similar-movies-scroll">
                  {similarMovies.map((similarMovie) => (
                    <Link
                      key={similarMovie.tmdb_id}
                      to={`/movies/${similarMovie.tmdb_id}`}
                      className="similar-movie-card"
                    >
                      <div className="similar-movie-poster">
                        {similarMovie.poster_url ? (
                          <img
                            src={similarMovie.poster_url}
                            alt={similarMovie.title}
                            loading="lazy"
                          />
                        ) : (
                          <div className="similar-movie-poster-placeholder">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" />
                            </svg>
                          </div>
                        )}
                        {(similarMovie.is_watched ||
                          similarMovie.is_want_to_watch ||
                          similarMovie.is_friends_watched) && (
                          <div className="explore-movie-badges">
                            {similarMovie.is_watched && (
                              <span className="explore-movie-badge explore-movie-badge-watched">
                                <svg
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="3"
                                >
                                  <path d="M20 6L9 17l-5-5" />
                                </svg>
                              </span>
                            )}
                            {similarMovie.is_want_to_watch && !similarMovie.is_watched && (
                              <span className="explore-movie-badge explore-movie-badge-want-to-watch">
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                                </svg>
                              </span>
                            )}
                            {similarMovie.is_friends_watched && (
                              <span className="explore-movie-badge explore-movie-badge-friend-watched">
                                <svg
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                  <circle cx="8.5" cy="7" r="4" />
                                  <path d="M20 8v6M23 11h-6" />
                                </svg>
                              </span>
                            )}
                          </div>
                        )}
                        {(similarMovie.imdb_rating ||
                          similarMovie.rotten_tomatoes_rating ||
                          similarMovie.metacritic_rating) && (
                          <div className="explore-movie-rating">
                            <RatingBadges
                              imdbRating={similarMovie.imdb_rating}
                              rottenTomatoesRating={similarMovie.rotten_tomatoes_rating}
                              metacriticRating={similarMovie.metacritic_rating}
                              compact={true}
                            />
                          </div>
                        )}
                      </div>
                      <span className="similar-movie-title">{similarMovie.title}</span>
                      {similarMovie.year && (
                        <span className="similar-movie-year">{similarMovie.year}</span>
                      )}
                    </Link>
                  ))}
                </div>

                <button
                  className="create-similar-playlist-button"
                  onClick={() => setShowSimilarPlaylistModal(true)}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Create Playlist from Similar Movies
                </button>
              </>
            )}
          </div>
        )}

        {/* Director, Writers & Country */}
        {(credits?.director ||
          (credits?.writers?.length ?? 0) > 0 ||
          (movie.production_countries?.length ?? 0) > 0) && (
          <div className="movie-crew-section">
            {credits?.director && (
              <div className="crew-item">
                <span className="crew-role">Director</span>
                <Link to={`/person/${credits.director.id}`} className="crew-name crew-name-link">
                  {credits.director.name}
                </Link>
              </div>
            )}
            {credits && credits.writers.length > 0 && (
              <div className="crew-item">
                <span className="crew-role">
                  {credits.writers.length > 1 ? 'Writers' : 'Writer'}
                </span>
                <span className="crew-name">
                  {credits.writers.map((w, index) => (
                    <span key={w.id}>
                      <Link to={`/person/${w.id}`} className="crew-name-link">
                        {w.name}
                      </Link>
                      {index < credits.writers.length - 1 && ', '}
                    </span>
                  ))}
                </span>
              </div>
            )}
            {movie.production_countries && movie.production_countries.length > 0 && (
              <div className="crew-item">
                <span className="crew-role">
                  {movie.production_countries.length > 1 ? 'Countries' : 'Country'}
                </span>
                <span className="crew-name">
                  {movie.production_countries.map((c) => c.name).join(', ')}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Release info */}
        <div className="movie-release-info">
          {movie.release_date && (
            <div className="info-item">
              <span className="info-label">Release Date</span>
              <span className="info-value">{formatDate(movie.release_date)}</span>
            </div>
          )}
          {movie.imdb_id && (
            <div className="info-item">
              <a
                href={`https://www.imdb.com/title/${movie.imdb_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="external-link"
              >
                View on IMDb →
              </a>
            </div>
          )}
        </div>

        {/* Cast Section */}
        {credits && credits.cast.length > 0 && (
          <div className="movie-cast-section">
            <h3>Cast</h3>
            <div className="cast-grid">
              {credits.cast.map((person) => (
                <Link
                  key={person.id}
                  to={`/person/${person.id}`}
                  className="cast-item cast-item-link"
                >
                  {person.profile_url ? (
                    <img src={person.profile_url} alt={person.name} className="cast-avatar" />
                  ) : (
                    <div className="cast-avatar cast-avatar-placeholder">
                      {person.name[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                  <div className="cast-info">
                    <span className="cast-name">{person.name}</span>
                    <span className="cast-character">{person.character}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Create Similar Playlist Modal */}
      {showSimilarPlaylistModal && movie && (
        <CreateSimilarPlaylistModal
          sourceMovie={movie}
          onClose={() => setShowSimilarPlaylistModal(false)}
        />
      )}

      {/* Add to Playlist Modal */}
      {showAddToPlaylistModal && movie && (
        <AddToPlaylistModal movie={movie} onClose={() => setShowAddToPlaylistModal(false)} />
      )}

      {/* Trailer Modal */}
      {showTrailerModal && movie && (
        <TrailerModal
          tmdbId={movie.tmdb_id}
          movieTitle={movie.title}
          onClose={() => setShowTrailerModal(false)}
        />
      )}
    </div>
  );
}
